import {Inject, Injectable, InjectionToken} from '@angular/core';
import {PlatformEnum} from '../../domain/platform';
import {SecureStorage, SecureStorageObject} from '@awesome-cordova-plugins/secure-storage/ngx';
import {Device} from '@capacitor/device';
import {StorageBackend} from '@openid/appauth';
import {AppConstants} from '../../commons/app-constants';
import {AppLanguageService} from '../app-language/app-language.service';
import {ToastService} from '../toast/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {isDefined} from '../../commons/utils';

export const LOCAL_STORAGE_TOKEN = new InjectionToken('LOCAL_STORAGE');

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService extends StorageBackend {
  private secureStorageObject: SecureStorageObject;
  private storage = new Map();
  private storageAvailable = false;
  private platform: PlatformEnum;

  constructor(private secureStorage: SecureStorage,
              private appLanguage: AppLanguageService,
              private toast: ToastService,
              private translate: TranslateService,
              @Inject(LOCAL_STORAGE_TOKEN) private localStorageProvider: any) {
    super();
  }

  private async getPlatform(): Promise<PlatformEnum> {
    const info = await Device.getInfo();
    return info.platform === 'web' ? PlatformEnum.WEB : PlatformEnum.MOBILE;
  }

  async initStorage(): Promise<void> {
    this.platform = await this.getPlatform();
    if (this.platform === PlatformEnum.MOBILE) {
      try {
        this.secureStorageObject = await this.secureStorage.create('secureStorage');
      } catch {
        this.storageAvailable = false;
        this.appLanguage.setTranslationLanguage().subscribe(() => {
          const messageKey = 'landing.secure-storage-unavailable';
          this.toast.errorWithCancelButton(
            this.translate.instant(messageKey)
          );
        });
        return;
      }
      const keys = await this.secureStorageObject.keys();
      await Promise.all(
        keys.map(async key => {
          try {
            const value = await this.secureStorageObject.get(key);
            this.storage.set(key, JSON.parse(value));
          } catch {
            // skip corrupt or unreadable entry; storage remains usable
          }
        })
      );
      this.storageAvailable = true;
    } else {
      this.storageAvailable = true;
    }
  }

  async setItem<T>(key: string, value: T): Promise<void> {
    const platform = await this.getPlatform();
    if (platform === PlatformEnum.MOBILE) {
      await this.secureStorageObject.set(key, JSON.stringify(value));
      this.storage.set(key, value);
    } else {
      this.localStorageProvider.setItem(key, JSON.stringify(value));
    }
  }

  getItem<T>(key: string): T {
    if (this.platform === PlatformEnum.MOBILE) {
      return this.storage.get(key);
    } else {
      const serializedObject = this.localStorageProvider.getItem(key);
      return serializedObject ? JSON.parse(serializedObject) : null;
    }
  }

  getKeys(): Promise<string[]> {
    return this.secureStorageObject.keys();
  }

  async removeItem(key: string): Promise<void> {
    const platform = await this.getPlatform();
    if (platform === PlatformEnum.MOBILE) {
      await this.secureStorageObject.remove(key);
      this.storage.delete(key);
    } else {
      this.localStorageProvider.removeItem(key);
    }
  }

  async clear(): Promise<void> {
    const platform = await this.getPlatform();
    if (platform === PlatformEnum.MOBILE) {
      const deviceId = this.getItem<string>(AppConstants.DEVICE_ID);
      const reviewFlagKey = this.getItem<boolean>(AppConstants.REVIEW_FLAG_KEY);
      try {
        if (isDefined(this.secureStorageObject)) {
          await this.secureStorageObject.clear();
        }
      } catch {
        // ignore secure-storage failure
      }
      this.storage.clear();
      try {
        if (isDefined(this.secureStorageObject)) {
          if (isDefined(deviceId)) {
            await this.setItem<string>(AppConstants.DEVICE_ID, deviceId);
          }
          if (isDefined(reviewFlagKey)) {
            await this.setItem<boolean>(AppConstants.REVIEW_FLAG_KEY, reviewFlagKey);
          }
        }
      } catch {
        // ignore restore failure
      }
    } else {
      this.localStorageProvider.clear();
    }
  }

  isStorageAvailable(): boolean {
    return this.storageAvailable;
  }
}

declare const localStorage: any;

export function localStorageFactory(): void {
  return localStorage;
}
